import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  McInput,
  McSelectNative,
  McPagination,
  McTag,
} from '@maersk-global/mds-react-wrapper';
import { demandsApi } from '../api/demandsApi';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { DemandStatusBadge } from '../components/DemandStatusBadge';
import {
  formatDemandStatus,
  formatCost,
} from '../utils/helpers/statusHelpers';
import type { DemandStatus } from '../types/engineering';
import { useLlmEvents } from '../hooks/useLlmEvents';

const PAGE_SIZE_OPTIONS = [20, 50, 70, 100];

const ALL_STATUSES: DemandStatus[] = [
  'BACKLOG', 'PLANNING', 'INPROGRESS', 'WAITING_REVIEW', 'DONE', 'TESTING', 'CANCELLED',
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...ALL_STATUSES.map((s) => ({ value: s, label: formatDemandStatus(s) })),
];

const PAGE_SIZE_SELECT_OPTIONS = PAGE_SIZE_OPTIONS.map((n) => ({
  value: n,
  label: `${n} / page`,
}));

export const DemandsListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');
  const [system, setSystem] = useState('');
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  // Auto-refresh demands list when ARCHITECT completes
  const handleLlmDone = useCallback((event: any) => {
    if (event.agent === 'ARCHITECT') {
      console.log('[DemandsListPage] ARCHITECT DONE detected, refreshing demands list');
      queryClient.invalidateQueries({ queryKey: ['demands'] });
    }
  }, [queryClient]);

  useLlmEvents('llm:done', handleLlmDone);

  const { data: systems } = useQuery({
    queryKey: ['demands', 'systems'],
    queryFn: () => demandsApi.getSystems(),
  });

  const systemOptions = useMemo(() => {
    const base = [{ value: '', label: 'All systems' }];
    const fromSystems = (systems ?? []).map((s) => ({ value: s, label: s }));
    return [...base, ...fromSystems];
  }, [systems]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['demands', 'paginated', { code, status, system, title, page, size }],
    queryFn: () =>
      demandsApi.getPaginated({
        code: code || undefined,
        status: status || undefined,
        system: system || undefined,
        title: title || undefined,
        page,
        size,
      }),
  });

  const resetPage = () => setPage(0);

  const totalElements = data?.totalElements ?? 0;
  const demands = data?.content ?? [];

  const renderTableContent = () => {
    if (isLoading) return <LoadingIndicator />;
    if (isError) return <div className="empty-state">Failed to load demands. Please try again later.</div>;
    if (demands.length === 0) return <div className="empty-state">No demands match the current filters.</div>;
    return null;
  };

  return (
    <div>
      <div className="page-header">
        <h1>Demands</h1>
        <p>All engineering demands — {totalElements} total</p>
      </div>

      {/* Filters */}
      <div className="demands-filters">
        <McInput
          label="Code"
          labelposition="left"
          fit="small"
          value={code}
          input={(e: Event) => {
            setCode((e.target as HTMLInputElement).value);
            resetPage();
          }}
          style={{ width: '120px' }}
        />
        <McSelectNative
          label="Status"
          labelposition="left"
          fit="small"
          options={STATUS_OPTIONS}
          value={[{ value: status, label: status || 'All statuses' }]}
          change={(e: Event) => {
            const detail = (e as CustomEvent).detail;
            setStatus(String(detail?.selectedOptions?.[0]?.value ?? ''));
            resetPage();
          }}
          style={{ width: '180px' }}
        />
        <McSelectNative
          label="System"
          labelposition="left"
          fit="small"
          options={systemOptions}
          value={[{ value: system, label: system || 'All systems' }]}
          change={(e: Event) => {
            const detail = (e as CustomEvent).detail;
            setSystem(String(detail?.selectedOptions?.[0]?.value ?? ''));
            resetPage();
          }}
          style={{ width: '180px' }}
        />
        <McInput
          label="Title"
          labelposition="left"
          fit="small"
          placeholder="Search..."
          value={title}
          input={(e: Event) => {
            setTitle((e.target as HTMLInputElement).value);
            resetPage();
          }}
          style={{ width: '200px' }}
        />
        <div style={{ flex: 1 }} />
        <McSelectNative
          label="Page size"
          labelposition="left"
          fit="small"
          options={PAGE_SIZE_SELECT_OPTIONS}
          value={[{ value: size, label: `${size} / page` }]}
          change={(e: Event) => {
            const detail = (e as CustomEvent).detail;
            setSize(Number(detail?.selectedOptions?.[0]?.value ?? 20));
            resetPage();
          }}
          style={{ width: '160px' }}
        />
      </div>

      {renderTableContent() ?? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="demands-table-wrapper">
            <table className="demands-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>System</th>
                  <th>Title</th>
                  <th>Cost</th>
                  <th>Tasks</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {demands.map((d) => (
                  <tr
                    key={d.id}
                    className="clickable"
                    onClick={() => navigate(`/demands/${d.id}`)}
                  >
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{d.code}</td>
                    <td>
                      <DemandStatusBadge status={d.status} />
                    </td>
                    <td>{d.systemName || '—'}</td>
                    <td>{d.title}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {d.cost != null ? formatCost(d.cost) : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{d.taskCount}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{d.percentConcluded}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <McPagination
            totalrecords={totalElements}
            pagesize={size}
            currentpage={page + 1}
            showpagesize={false}
            showpagestatus
            variant="select"
            pagechange={(e: Event) => {
              const newPage = (e as CustomEvent<number>).detail;
              setPage(newPage - 1);
            }}
          />
        </div>
      )}
    </div>
  );
};

